import { Wrench } from "lucide-react";
import LandingHeader from "@/components/landing/LandingHeader";
import LandingFooter from "@/components/landing/LandingFooter";
import {
  UseCaseHero,
  UseCaseContext,
  UseCaseProblems,
  UseCaseSolution,
  UseCaseResults,
  UseCaseTestimonial,
  UseCaseCTA,
  RelatedUseCases,
} from "@/components/use-cases";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/seo/JsonLd";

const ACCENT_COLOR = "#3B82F6";

const problems = [
  "Factures fournisseurs qui arrivent de partout (email, courrier, comptoir)",
  "Equipes qui tournent sur plusieurs chantiers dans la meme journee",
  "Impossible de consolider les couts en temps reel",
  "Decouverte des marges reelles 2 mois apres facturation",
];

const solutions = [
  {
    title: "Centralisation Scan IA",
    description: "Toutes les factures au meme endroit, extraites automatiquement",
  },
  {
    title: "Affectations demi-journee",
    description: "Qui est ou, matin et apres-midi",
  },
  {
    title: "Consolidation temps reel",
    description: "Marge de chaque chantier visible instantanement",
  },
  {
    title: "Alertes marge faible",
    description: "Notification si un chantier passe sous 15%",
  },
];

const results = [
  {
    metric: "Temps gestion factures",
    before: "4h/sem",
    after: "1h30/sem",
  },
  {
    metric: "Chantiers deficitaires detectes",
    before: "Apres coup",
    after: "En temps reel",
  },
  {
    metric: "Factures perdues/oubliees",
    before: "2-3/mois",
    after: "0",
  },
];

const Plombier = () => {
  usePageMeta({
    title: "Logiciel gestion chantier plombier — B8ild",
    description: "Suivi financier pour plombiers et chauffagistes. Chantiers pompe à chaleur, plomberie, chauffage. Rentabilité temps réel.",
    url: "https://b8ild.fr/use-cases/plombier",
  });

  return (
    <div className="min-h-screen bg-[#0A0A0F] overflow-x-hidden">
      <LandingHeader />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://b8ild.fr/" },
          { "@type": "ListItem", position: 2, name: "Cas d'usage", item: "https://b8ild.fr/use-cases" },
          { "@type": "ListItem", position: 3, name: "Plombier", item: "https://b8ild.fr/use-cases/plombier" },
        ],
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Logiciel gestion chantier plombier — B8ild",
        description: "Suivi financier pour plombiers et chauffagistes. Chantiers pompe à chaleur, plomberie, chauffage. Rentabilité temps réel.",
        url: "https://b8ild.fr/use-cases/plombier",
        isPartOf: { "@type": "WebSite", name: "B8ild", url: "https://b8ild.fr" },
        inLanguage: "fr",
      }} />

      <UseCaseHero
        badge="Plombier-Chauffagiste"
        title="Sophie pilote 6 plombiers"
        subtitle="sur 15 chantiers. Sans stress."
        description="Decouvrez comment B8ild aide les entreprises de plomberie a gerer leurs equipes et leur rentabilite."
        icon={Wrench}
        accentColor={ACCENT_COLOR}
        personaInitials="SL"
        personaName="Sophie"
        personaRole="TPE plomberie-chauffage, 6 personnes"
        personaCity="Marseille · CA ~450 000 EUR/an"
      />

      <UseCaseContext
        context="Sophie a repris l'entreprise de son pere il y a 5 ans. Elle gere 4 plombiers, 1 apprenti et une secretaire a mi-temps. Entre les depannages urgents et les chantiers de renovation salle de bain, elle doit jongler avec 10 a 15 chantiers actifs en permanence."
        accentColor={ACCENT_COLOR}
      />

      <UseCaseProblems
        problems={problems}
        accentColor={ACCENT_COLOR}
      />

      <UseCaseSolution
        solutions={solutions}
        accentColor={ACCENT_COLOR}
      />

      <UseCaseResults
        results={results}
        accentColor={ACCENT_COLOR}
        period="3 mois"
      />

      <UseCaseTestimonial
        quote="Je gagne 2h par semaine sur la gestion des factures. Et surtout, j'ai detecte 3 chantiers qui allaient etre deficitaires. J'ai pu reagir a temps."
        name="Sophie"
        role="Gerante"
        city="Marseille"
        initials="SL"
        accentColor={ACCENT_COLOR}
      />

      {/* Spécificités métier */}
      <section className="py-16 bg-[#0A0A0F]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">
              Vos chantiers, on les connaît
            </h2>
            <p className="text-white/60 leading-relaxed">
              Pompe à chaleur, chaudière, plancher chauffant, VMC, plomberie sanitaire, remplacement colonne montante — B8ild suit la rentabilité de chacun. En rénovation énergétique, les aides MaPrimeRénov' chauffage et primes CEE impactent directement vos marges. Avec B8ild, vous voyez le vrai résultat financier de chaque chantier, pas une estimation 3 mois après.
            </p>
          </div>
        </div>
      </section>

      {/* Cas concret */}
      <section className="py-16 bg-[#0A0A0F]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">
              Cas concret : PAC, MaPrimeRénov' et la marge qui fond
            </h2>
            <div className="space-y-4 text-white/60 leading-relaxed">
              <p>
                Sophie installe des pompes à chaleur depuis 2 ans. Son dernier chantier : une PAC air-eau à 14 500€ TTC pour un pavillon de 120m². Le client touche 5 000€ de MaPrimeRénov' et 2 500€ de prime CEE. Il ne paie que 7 000€ de sa poche. Mais les coûts de Sophie restent les mêmes : PAC achetée 5 200€, main d'œuvre (elle + 1 ouvrier, 5 jours) : 3 600€, fluide frigorigène : 350€, mise en service : 400€, déplacement : 250€.
              </p>
              <p>
                Total coûts : 9 800€. Son CA HT est 12 083€ (14 500€ TTC / 1.2). Sa marge brute : 2 283€, soit 18,9%. Sauf que Sophie a oublié de compter 1,5 jour de retour pour une fuite sur le raccordement DTU 60.1. Coût : 540€ de main d'œuvre + 85€ de pièces. Marge réelle : 1 658€, soit 13,7%.
              </p>
              <p>
                Sur 8 PAC par trimestre, chaque point de marge perdu coûte 970€. 4 points perdus × 4 trimestres = 15 500€/an de manque à gagner. Avec un suivi en temps réel, Sophie détecte la dérive dès le retour sur site et ajuste ses devis suivants.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Questions métier */}
      <section className="py-16 bg-[#08080C]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8">
              Questions fréquentes — Plombiers
            </h2>
            <div className="space-y-6">
              {[
                {
                  q: "Quelle marge sur l'installation d'une pompe à chaleur ?",
                  a: "La marge brute sur une PAC air-eau tourne entre 15% et 25% selon le modèle et la complexité. Les PAC air-air sont plus rentables (20-30%) car l'installation est plus simple. Attention aux retours sur site : chaque intervention non prévue mange 2-3 points de marge. B8ild suit le coût réel de chaque chantier PAC, y compris les retours.",
                },
                {
                  q: "Comment facturer avec MaPrimeRénov' et les CEE ?",
                  a: "Facturez le montant TTC total au client. Les primes sont versées directement au client (ou à vous si vous avez un mandat de versement). Le piège : certains artisans calculent leur marge sur le montant client au lieu du HT total. Votre marge se calcule sur votre CA HT réel, pas sur ce que le client paie.",
                },
                {
                  q: "Quel taux horaire pour un plombier-chauffagiste en 2026 ?",
                  a: "Entre 40€ et 70€/h HT. Plomberie courante : 40-50€/h. Chauffage et PAC : 50-65€/h. Dépannage et urgence : 60-90€/h. Le coût salarié réel d'un ouvrier plombier N3 est d'environ 23-26€/h charges comprises. Votre taux doit couvrir ce coût + frais généraux + marge.",
                },
              ].map((faq, i) => (
                <div key={i} className="border-b border-white/[0.06] pb-6">
                  <h3 className="text-lg font-medium text-white mb-3">{faq.q}</h3>
                  <p className="text-white/50 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <UseCaseCTA accentColor={ACCENT_COLOR} />

      <RelatedUseCases currentSlug="plombier" />

      <LandingFooter />
    </div>
  );
};

export default Plombier;
